import { client } from "./generated/client.gen";
import { postAuthRefreshToken } from "./generated/sdk.gen";
import { API_URL } from "@/lib/config";

client.setConfig({
  baseUrl: `${API_URL}/api/v1`,
});

let refreshPromise: Promise<boolean> | null = null;

function isTokenExpiringSoon(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiresAt = payload.exp * 1000;
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() > expiresAt - fiveMinutes;
  } catch {
    return true;
  }
}

async function refreshTokens(): Promise<boolean> {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return false;

  const { data, error } = await postAuthRefreshToken({
    body: { refresh_token: refreshToken },
  });

  if (error || !data) return false;

  localStorage.setItem("access_token", data.data.access_token);
  localStorage.setItem("refresh_token", data.data.refresh_token);
  return true;
}

function logout() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("organization-preferences");
  window.location.href = "/auth/login";
}

client.interceptors.request.use(async (request) => {
  const url = new URL(request.url);
  const path = url.pathname.replace(/^\/api\/v1/, "");
  if (path.startsWith("/auth/") && path !== "/auth/me") {
    return request;
  }

  const token = localStorage.getItem("access_token");
  if (token && isTokenExpiringSoon(token)) {
    if (!refreshPromise) {
      refreshPromise = refreshTokens().finally(() => {
        refreshPromise = null;
      });
    }
    const success = await refreshPromise;
    if (!success) {
      logout();
      return request;
    }
  }

  const currentToken = localStorage.getItem("access_token");
  if (currentToken) {
    request.headers.set("Authorization", `Bearer ${currentToken}`);
  }

  // Inject current organization ID from persisted store
  try {
    const stored = localStorage.getItem("organization-preferences");
    if (stored) {
      const parsed = JSON.parse(stored);
      const orgId = parsed?.state?.currentOrganizationId;
      if (orgId) {
        request.headers.set("X-Organization-Id", orgId);
      }
    }
  } catch {
    // ignore parse errors
  }

  return request;
});

client.interceptors.response.use(async (response, request) => {
  if (response.status !== 401) return response;

  const url = new URL(request.url);
  const responsePath = url.pathname.replace(/^\/api\/v1/, "");
  if (responsePath.startsWith("/auth/")) return response;

  if (!refreshPromise) {
    refreshPromise = refreshTokens().finally(() => {
      refreshPromise = null;
    });
  }

  const success = await refreshPromise;

  if (!success) {
    logout();
    return response;
  }

  const retryRequest = new Request(request, {
    headers: new Headers(request.headers),
  });
  retryRequest.headers.set(
    "Authorization",
    `Bearer ${localStorage.getItem("access_token")}`
  );

  return fetch(retryRequest);
});

export { client };
